import React, { useContext, type FC } from 'react';

import { isDefined } from '@lucid/utils';
import { useSmartlingStrings } from '@lucid/hooks';

import Heading from '../heading/heading';
import Container from '../container/container';
import LayoutContext from '../context/layout-context';
import ResourceCard from '../cards/resource-card/resource-card';
import Pagination from '../pagination/pagination';
import type { Lucid } from '../../types/types';

export interface ClassificationGroupListProps {
  groups?: (Lucid.Directus.Generated.Blog_Category | Lucid.Directus.Generated.Blog_Collection)[];
}

const ClassificationGroupList: FC<ClassificationGroupListProps> = ({ groups }) => {
  const { shortCode } = useContext(LayoutContext);

  const withSmartling = useSmartlingStrings({
    keys: ['mktg_blog_choose_collection', 'mktg_blog_choose_category', 'mktg_blog_read_more', 'mktg_read_more_about'],
    shortCode,
  });

  const filteredGroups = groups
    ?.map(group => {
      const translation = group.translations?.find(tr => tr.languages_code?.short_code === shortCode);

      if (translation) {
        return { id: group.id, translations: [translation] };
      }

      return null;
    })
    .filter(isDefined);

  if (!filteredGroups?.length) {
    return null;
  }
  const groupType = groups?.[0]?.['__typename']?.replace('DirectusData_blog_', '');

  return (
    <Container
      css={theme => ({ paddingTop: theme.space.s120, paddingBottom: theme.space.s120 })}
      data-testid="category-buttons"
    >
      <Heading as="h2" css={theme => ({ marginBottom: theme.space.s48 })} variant="headingXL">
        {withSmartling(`mktg_blog_choose_${groupType}`)}
      </Heading>
      <Pagination aria-label="blog category list" items={filteredGroups} itemsPerPage={9}>
        {({ visibleItems }) => (
          <ul
            css={theme => ({
              listStyle: 'none',
              margin: '0',
              display: 'grid',
              gap: theme.space.s40,
              width: '100%',
              [theme.breakpoints.bp768.min]: {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
              [theme.breakpoints.bp1024.min]: {
                gridTemplateColumns: 'repeat(3, 1fr)',
              },
            })}
          >
            {visibleItems.map((category: Lucid.Directus.Generated.Blog_Category) => (
              <li key={`category-group-${category.id}`}>
                <ResourceCard
                  css={{ height: '100%' }}
                  heading={category.translations?.[0].name}
                  link={{
                    default_link_text: withSmartling('mktg_blog_read_more'),
                    final_url: category.translations?.[0].path?.url,
                    accessibleDescription: withSmartling('mktg_read_more_about', {
                      title: category.translations?.[0].name || '',
                    }),
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </Pagination>
    </Container>
  );
};

export default ClassificationGroupList;
export { ClassificationGroupList };
